import {
  TimerConfig,
  TimerAlertsConfig,
  RoundConfig,
  ActivityState,
} from '@/redux/slices/timerConfigurationSlice'

export type { RoundConfig, TimerAlertsConfig }

export enum TimerMode {
  SINGLE_ROUND = 'single_round',
  SINGLE_ROUND_ADVISOR_ONLY = 'single_round_advisor_only',
  MULTI_ROUND_ALL = 'multi_round_all',
  MULTI_ROUND_NEGOTIATION_LOCKED = 'multi_round_negotiation_locked',
  MANUAL_MODE = 'manual_mode',
  TEAM_MODE = 'team_mode',
  RESEARCH_MODE = 'research_mode',
}

export const TimerModeLabels: Record<TimerMode, string> = {
  [TimerMode.SINGLE_ROUND]: 'Single Round',
  [TimerMode.SINGLE_ROUND_ADVISOR_ONLY]: 'Single Round (Advisor Only)',
  [TimerMode.MULTI_ROUND_ALL]: 'Multi-Round (All Features)',
  [TimerMode.MULTI_ROUND_NEGOTIATION_LOCKED]:
    'Multi-Round (Negotiation Locked)',
  [TimerMode.MANUAL_MODE]: 'Manual Mode',
  [TimerMode.TEAM_MODE]: 'Team Mode',
  [TimerMode.RESEARCH_MODE]: 'Research Mode',
}

export const defaultRoundConfig: RoundConfig = {
  roundNumber: 0,
  status: ActivityState.NotStarted,
  randrConsultation: 10,
  advisorConsultation: 15,
  negotiation: 30,
  decisionSubmission: 15,
  isRandR: true,
  autoReleaseEnabled: false,
  autoReleaseEventIds: [],
  isRandrInfinite: false,
  isAdvisorInfinite: false,
  isNegotiationInfinite: false,
  isDecisionInfinite: false,
}

export const createDefaultMultiRoundConfig = (
  numberOfRounds: number
): RoundConfig[] => {
  const configs: RoundConfig[] = []

  configs.push({
    ...defaultRoundConfig,
    isRandR: true,
  })

  for (let i = 1; i < numberOfRounds; i++) {
    configs.push({
      advisorConsultation: 15,
      negotiation: 30,
      decisionSubmission: 15,
      isRandR: false,
      autoReleaseEnabled: false,
      autoReleaseEventIds: [],
      isRandrInfinite: false,
      isAdvisorInfinite: false,
      isNegotiationInfinite: false,
      isDecisionInfinite: false,
    })
  }

  return configs
}

export const defaultTimerAlerts: TimerAlertsConfig = {
  enableFiveMinuteVisual: false,
  enableOneMinuteVisual: true,
}

export const defaultConfig: TimerConfig = {
  mode: TimerMode.SINGLE_ROUND,
  numberOfRounds: 1,
  roundConfigurations: [defaultRoundConfig],
  alerts: defaultTimerAlerts,
}

export const calculateTotalGameplayLength = (config: TimerConfig): number => {
  const hasInfinitePhase = config.roundConfigurations.some(
    (round) =>
      round.isRandrInfinite ||
      round.isAdvisorInfinite ||
      round.isNegotiationInfinite ||
      round.isDecisionInfinite
  )

  if (hasInfinitePhase) {
    return Infinity
  }

  return config.roundConfigurations.reduce((total, round, index) => {
    let roundTotal = round.advisorConsultation

    if (config.mode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY) {
      return total + roundTotal
    }

    if (round.isRandR && round.randrConsultation) {
      roundTotal += round.randrConsultation
    }

    roundTotal += round.decisionSubmission

    if (
      config.mode !== TimerMode.MULTI_ROUND_NEGOTIATION_LOCKED ||
      index === 0
    ) {
      roundTotal += round.negotiation
    }

    return total + roundTotal
  }, 0)
}

export const isPhaseInfinite = (round: RoundConfig, phase: string): boolean => {
  switch (phase) {
    case 'randrConsultation':
      return round.isRandrInfinite || false
    case 'advisorConsultation':
      return round.isAdvisorInfinite || false
    case 'negotiation':
      return round.isNegotiationInfinite || false
    case 'decisionSubmission':
      return round.isDecisionInfinite || false
    default:
      return false
  }
}

export const formatPhaseTime = (
  minutes: number,
  isInfinite: boolean
): string => {
  if (isInfinite) {
    return '∞'
  }
  return `${minutes} min${minutes !== 1 ? 's' : ''}`
}
